import fc from 'fast-check';
import reactUpdate from 'immutability-helper';

import {MinimalFolder, NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {NavStoreRegistry, RegistrySummary} from 'constants/RegistryConstants';
import {
  FolderNode,
  RegistryLabelingStrategy,
  RegistryNode,
  SourceInterface,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  RegistrySummaryQuery,
  RegistrySummaryQueryVariables,
} from 'registry/graphql/__generated/RegistrySummaryQuery';
import {REGISTRY_SUMMARY_QUERY} from 'registry/graphql/RegistrySummary';
import {ORGANIZATION, ORGANIZATION_NODE} from 'test/fixtures/parties/OrganizationFixtures';
import {SOURCE_USER_SUMMARY, SOURCE_USER_SUMMARY_GQL, USER} from 'test/fixtures/parties/UserFixtures';
import {
  DEFAULT_NAV_STORE_SOURCE_PERMISSIONS_REST,
  DEFAULT_NAV_STORE_SOURCE_PERMISSIONS_GQL,
  FULL_PERMISSIONS_GRAPHQL,
} from 'test/fixtures/PermissionFixtures';
import {WRITE_POLICY} from 'test/fixtures/PolicyFixtures';
import {makeTypesafeQueryMock, TypesafeMockedResponse} from 'test/lib/MockGraphQLProvider';

import {NavStoreRegistryGraphQL} from '../../../../graphql-fragments/nav/__generated/NavStoreRegistryGraphQL';
import {FolderSummaryGraphQL} from '../../../../graphql-fragments/nav/folders/__generated/FolderSummaryGraphQL';
import {RegistryGraphQL} from '../../../../graphql-fragments/registry/__generated/RegistryGraphQL';
import {RegistrySummaryGraphQL} from '../../../../graphql-fragments/registry/__generated/RegistrySummaryGraphQL';

export const LabelingStrategyArbitrary = fc.oneof(
  ...Object.keys(RegistryLabelingStrategy).map((key: keyof typeof RegistryLabelingStrategy) =>
    fc.constant(RegistryLabelingStrategy[key])
  )
);

export const REGISTRY_SUMMARY: RegistrySummary = {
  archived: false,
  archiveRecord: null,
  areSettingsAdminOnly: false,
  createdAt: '2016-11-08T19:05:48.402226+00:00',
  creator: SOURCE_USER_SUMMARY,
  disablePersonalCollections: true,
  useStrictValidation: true,
  hidden: false,
  id: 'src_IIHi0wVy',
  locked: false,
  modifiedAt: '2016-11-08T19:06:30.258610+00:00',
  name: 'Enterprise Registry',
  ownerInfo: {
    memberDataPolicyId: WRITE_POLICY.id,
    type: 'organization',
  },
  public: false,
  alwaysShowAncestorFolderNames: true,
  rootFolder: {
    archiveRecord: null,
    api_identifier: 'lib_Tsur0TI6',
    created_at: '2016-11-08T19:05:48.402226+00:00',
    description: null,
    id: 23,
    dbId: 23,
    name: 'Enterprise Registry',
    owner: {
      avatar_url: '/static/img/no_avatar.jpg',
      handle: 'testOrg',
      id: 'ent_6AF4sRH3',
      institution: null,
      joinedOn: '2016-11-08T19:05:41.247326+00:00',
      name: 'testOrg',
      type: ModelTypeConstants.ORGANIZATION,
      url_identifier: 'testorg',
    },
    owner_handle: 'testOrg',
    owner_url: '/testorg/',
    owner_url_identifier: 'testorg',
    parent: null,
    type: 'ALL',
    url: '/testorg/f_/Tsur0TI6-enterprise-registry/',
  },
  s_owner: ORGANIZATION,
  sourceType: 'registry',
  starred: false,
  url: '/testorg/f_/Tsur0TI6-enterprise-registry/',
};

// This returns Source.to_dict_with_perms(include_collaborators=True) for a registry.
export const REGISTRY: NavStoreRegistry = {
  ...REGISTRY_SUMMARY,
  collaborators: [USER],
  description: null,
  permissions: DEFAULT_NAV_STORE_SOURCE_PERMISSIONS_REST,
};

export const FOLDERS: Array<NumberIDFolder> = [
  {
    api_identifier: 'lib_LL2mWCBC',
    created_at: '2016-11-08T19:05:48.402226+00:00',
    description: null,
    id: 22,
    dbId: 22,
    name: 'Enterprise Registry',
    owner: {
      avatar_url: '/static/img/no_avatar.jpg',
      handle: 'benchling',
      id: 'ent_9vbEqh2J',
      institution: null,
      joinedOn: '2016-11-08T19:05:41.189484+00:00',
      name: 'Benchling Lab',
      type: ModelTypeConstants.ORGANIZATION,
      url_identifier: 'benchling',
    },
    owner_handle: 'benchling',
    owner_url: '/benchling/',
    owner_url_identifier: 'benchling',
    parent: null,
    source: REGISTRY,
    type: 'ALL',
    url: '/testorg/f_/Tsur0TI6-enterprise-registry/',
  },
];

export const REGISTRY_ADMIN = reactUpdate(REGISTRY, {
  permissions: {admin: {$set: true}, locationsCreatable: {$set: true}},
});

export const REGISTRY_ROOT: StringIDFolder = {
  ...REGISTRY.rootFolder,
  id: REGISTRY.rootFolder.api_identifier,
  source: FOLDERS[0].source,
};

export const MINIMAL_REGISTRY_ROOT: MinimalFolder = {
  archiveRecord: null,
  api_identifier: 'lib_Tsur0TI6',
  id: 'lib_Tsur0TI6',
  name: 'Enterprise Registry',
  owner_handle: 'testOrg',
  parent: null,
  type: 'ALL',
  source: REGISTRY,
};

export const REGISTRY_WITH_ORGANIZATION_GQL: RegistryGraphQL = {
  __typename: 'RegistryNode',
  id: REGISTRY_SUMMARY.id,
  organization: {
    __typename: 'OrganizationNode',
    id: ORGANIZATION.id,
  },
};

export const REGISTRY_SUMMARY_GQL: RegistrySummaryGraphQL = {
  __typename: 'RegistryNode',
  archived: false,
  archiveRecord: null,
  areSettingsAdminOnly: false,
  createdAt: '2016-11-08T19:05:48.402226+00:00',
  creator: SOURCE_USER_SUMMARY_GQL,
  disablePersonalCollections: true,
  useStrictValidation: true,
  hidden: false,
  id: 'src_IIHi0wVy',
  locked: false,
  modifiedAt: '2016-11-08T19:06:30.258610+00:00',
  name: 'Enterprise Registry',
  ownerInfoJson: {
    memberDataPolicyId: WRITE_POLICY.id,
    type: 'organization',
  },
  public: false,
  alwaysShowAncestorFolderNames: true,
  rootFolder: {
    __typename: 'FolderNode',
    archiveRecord: null,
    id: 'lib_Tsur0TI6',
    createdAt: '2016-11-08T19:05:48.402226+00:00',
    description: null,
    dbId: 23,
    name: 'Enterprise Registry',
    ownerJson: {
      avatar_url: '/static/img/no_avatar.jpg',
      handle: 'testOrg',
      id: 'ent_6AF4sRH3',
      institution: null,
      joinedOn: '2016-11-08T19:05:41.247326+00:00',
      name: 'testOrg',
      type: 'organization',
      url_identifier: 'testorg',
    },
    ownerUrl: '/testorg/',
    parentFolders: [],
    type: 'ALL',
    url: '/testorg/f_/Tsur0TI6-enterprise-registry/',
  },
  ownerJson: ORGANIZATION,
  sourceType: 'registry',
  starred: false,
  url: '/testorg/f_/Tsur0TI6-enterprise-registry/',
};

export const REGISTRY_SUMMARY_GQL_USE_STRICT_VALIDATION_FALSE: RegistrySummaryGraphQL = {
  ...REGISTRY_SUMMARY_GQL,
  useStrictValidation: false,
};

export const REGISTRY_GQL: NavStoreRegistryGraphQL = {
  ...REGISTRY_SUMMARY_GQL,
  collaboratorsJson: [USER],
  description: null,
  permissions: {
    ...DEFAULT_NAV_STORE_SOURCE_PERMISSIONS_GQL,
    admin: false,
    locationsCreatable: false,
    canManageCollaborators: false,
  },
};

export const REGISTRY_WITH_ONLY_ID_GQL = {
  __typename: 'RegistryNode' as const,
  id: REGISTRY.id,
};

export const REGISTRY_ADMIN_GQL = reactUpdate(REGISTRY_GQL, {
  permissions: {admin: {$set: true}, locationsCreatable: {$set: true}},
});

export function makeRegistrySummaryMock(registryId: string): TypesafeMockedResponse {
  return makeTypesafeQueryMock<RegistrySummaryQuery, RegistrySummaryQueryVariables>({
    query: REGISTRY_SUMMARY_QUERY,
    variables: {registryId},
    data: {
      registry: REGISTRY_SUMMARY_GQL,
    },
  });
}

export const REGISTRY_ROOT_GQL: FolderSummaryGraphQL = {
  ...REGISTRY_GQL.rootFolder,
  source: REGISTRY_SUMMARY_GQL,
  parentFolders: [],
};

// Full fixtures for composability. Contains all fields of the GraphQL types.
// Use these to back GraphQL queries that only fetch parts of these objects.
// RegistryNode and FolderNode have a circular reference (FolderNode.source -> RegistryNode -> FolderNode),
// so we use a factory to wire them together.
function createRegistryAndFolderNodes(): {registry: RegistryNode; folder: FolderNode} {
  const registry: RegistryNode = {
    __typename: 'RegistryNode',
    alwaysShowAncestorFolderNames: REGISTRY_SUMMARY_GQL.alwaysShowAncestorFolderNames,
    antibodySchemas: [],
    archiveRecord: REGISTRY_SUMMARY_GQL.archiveRecord,
    archived: REGISTRY_SUMMARY_GQL.archived,
    areSettingsAdminOnly: REGISTRY_SUMMARY_GQL.areSettingsAdminOnly,
    collaboratorsJson: [USER],
    createdAt: REGISTRY_SUMMARY_GQL.createdAt,
    creator: {
      ...SOURCE_USER_SUMMARY_GQL,
      tenantCollaborations: [],
    },
    creatorJson: null,
    dataModsEnabled: false,
    description: null,
    disablePersonalCollections: REGISTRY_SUMMARY_GQL.disablePersonalCollections,
    hidden: REGISTRY_SUMMARY_GQL.hidden,
    id: REGISTRY_SUMMARY_GQL.id,
    inheritedCollaborations: [],
    locationSchemas: [],
    locked: REGISTRY_SUMMARY_GQL.locked,
    modifiedAt: REGISTRY_SUMMARY_GQL.modifiedAt,
    name: REGISTRY_SUMMARY_GQL.name,
    numBioentityValidationFailures: 0,
    numLocationValidationFailures: 0,
    organization: ORGANIZATION_NODE,
    ownerInfoJson: REGISTRY_SUMMARY_GQL.ownerInfoJson,
    ownerJson: ORGANIZATION,
    permissions: FULL_PERMISSIONS_GRAPHQL,
    public: REGISTRY_SUMMARY_GQL.public,
    reviewSummaryJson: null,
    rootFolder: null as unknown as FolderNode, // Will be set below
    schemaFieldSelectors: [],
    sourceType: REGISTRY_SUMMARY_GQL.sourceType,
    starred: REGISTRY_SUMMARY_GQL.starred,
    tagSchemas: [],
    url: REGISTRY_SUMMARY_GQL.url,
    useStrictValidation: REGISTRY_SUMMARY_GQL.useStrictValidation,
  };

  const folder: FolderNode = {
    __typename: 'FolderNode',
    archiveRecord: REGISTRY_SUMMARY_GQL.rootFolder.archiveRecord,
    collaboratorsJson: [],
    createdAt: REGISTRY_SUMMARY_GQL.rootFolder.createdAt,
    dbId: REGISTRY_SUMMARY_GQL.rootFolder.dbId,
    description: REGISTRY_SUMMARY_GQL.rootFolder.description,
    id: REGISTRY_SUMMARY_GQL.rootFolder.id,
    inheritedCollaborations: [],
    modifiedAt: REGISTRY_SUMMARY_GQL.modifiedAt,
    name: REGISTRY_SUMMARY_GQL.rootFolder.name,
    ownerJson: REGISTRY_SUMMARY_GQL.rootFolder.ownerJson,
    ownerUrl: REGISTRY_SUMMARY_GQL.rootFolder.ownerUrl,
    parent: null,
    parentFolders: [],
    permissions: FULL_PERMISSIONS_GRAPHQL,
    source: registry as SourceInterface,
    sourceJson: {},
    star: null,
    type: REGISTRY_SUMMARY_GQL.rootFolder.type,
    url: REGISTRY_SUMMARY_GQL.rootFolder.url,
  };

  registry.rootFolder = folder;

  return {registry, folder};
}

const {registry: REGISTRY_NODE, folder: FOLDER_NODE} = createRegistryAndFolderNodes();
export {REGISTRY_NODE, FOLDER_NODE};
